import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import {
  UsersThree,
  Heartbeat,
  Drop,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import LoginModal from './LoginModal';
import UserMenu from './UserMenu';
import DesktopMoreDropdown from './nav/DesktopMoreDropdown';
import BottomTabBar from './nav/BottomTabBar';
import RomyIcon from './RomyIcon';

const isNative = Capacitor.isNativePlatform();
const isAndroid = /android/i.test(navigator.userAgent);
const APP_DOWNLOAD_URL = isAndroid
  ? 'https://play.google.com/store/apps/details?id=health.leena.app'
  : 'https://apps.apple.com/us/app/romy/id6760265825';

interface AppNavProps {
  onLoginOpen?: () => void;
}

const PRIMARY_LINKS = [
  { to: '/community', label: 'Community', icon: UsersThree, matchPrefix: '/community' },
  { to: '/health', label: 'Health', icon: Heartbeat, matchPrefix: '/health' },
  { to: '/health/cycle', label: 'Cycle', icon: Drop, matchPrefix: '/health/cycle' },
];

export default function AppNav({ onLoginOpen }: AppNavProps) {
  const { currentUser } = useAuth();
  const location = useLocation();
  const [isLoginOpen, setIsLoginOpen] = useState(false);

  const openLogin = () => {
    setIsLoginOpen(true);
    onLoginOpen?.();
  };

  const isActive = (matchPrefix: string) =>
    location.pathname.startsWith(matchPrefix) &&
    // Most specific match wins — don't highlight /health when on /health/cycle
    !PRIMARY_LINKS.some(l => l.matchPrefix !== matchPrefix && l.matchPrefix.length > matchPrefix.length && location.pathname.startsWith(l.matchPrefix));

  // Signed-in mobile web users get app-like mode (no mobile header)
  const appLikeMobile = !isNative && !!currentUser;

  useEffect(() => {
    if (appLikeMobile) {
      document.documentElement.setAttribute('data-app-mode', '');
    } else {
      document.documentElement.removeAttribute('data-app-mode');
    }
    return () => document.documentElement.removeAttribute('data-app-mode');
  }, [appLikeMobile]);

  return (
    <>
      {!isNative && (
        <header className={`fixed top-0 left-0 right-0 z-50 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 ${appLikeMobile ? 'hidden md:block' : ''}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Desktop layout */}
            <div className="hidden md:flex justify-between items-center h-16">
              <Link to={currentUser ? '/chat' : '/'} className="flex items-center gap-2">
                <svg width="24" height="24" viewBox="0 0 400 400" fill="none" aria-hidden="true">
                  <defs>
                    <linearGradient id="nav-bm" x1="40" y1="360" x2="360" y2="40" gradientUnits="userSpaceOnUse">
                      <stop offset="0%" stopColor="#FF6B9D" />
                      <stop offset="100%" stopColor="#FF8C69" />
                    </linearGradient>
                  </defs>
                  <circle cx="200" cy="200" r="190" fill="url(#nav-bm)" />
                  <circle cx="200" cy="159" r="70" fill="white" />
                  <path d="M 200 123 L 214 150 L 241 159 L 214 168 L 200 195 L 186 168 L 159 159 L 186 150 Z" fill="#EC4899" />
                  <rect x="192" y="229" width="16" height="120" rx="8" fill="white" />
                  <path d="M 200 303 C 178 299, 136 279, 116 253" stroke="white" strokeWidth="16" fill="none" strokeLinecap="round" />
                  <path d="M 200 303 C 222 299, 264 279, 284 253" stroke="white" strokeWidth="16" fill="none" strokeLinecap="round" />
                </svg>
                <span className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-orange-500 to-pink-500 bg-clip-text text-transparent tracking-tight">
                  Romy
                </span>
              </Link>

              <nav className="flex items-center space-x-4 lg:space-x-6">
                {PRIMARY_LINKS.map(({ to, label, icon: Icon, matchPrefix }) => (
                  <Link
                    key={to}
                    to={to}
                    className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg border transition-colors ${
                      isActive(matchPrefix)
                        ? 'bg-pink-100 dark:bg-pink-900/40 border-pink-300 dark:border-pink-700 text-gray-800 dark:text-gray-100'
                        : 'border-transparent text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                  >
                    <Icon
                      size={20}
                      weight={isActive(matchPrefix) ? 'fill' : 'regular'}
                      className={isActive(matchPrefix) ? 'text-pink-600 dark:text-pink-400' : undefined}
                    />
                    <span className={isActive(matchPrefix) ? 'font-medium' : undefined}>
                      {label}
                    </span>
                  </Link>
                ))}
                <DesktopMoreDropdown />
                <Link
                  to="/chat"
                  className={`px-3 lg:px-4 py-2 rounded-lg flex items-center space-x-2 text-sm lg:text-base transition-all ${
                    location.pathname.startsWith('/chat')
                      ? 'bg-pink-100 dark:bg-pink-900/30 border border-pink-300 dark:border-pink-700 text-gray-800 dark:text-gray-100'
                      : 'bg-gradient-to-r from-orange-500 to-pink-500 text-white hover:from-orange-600 hover:to-pink-600 shadow-lg hover:shadow-xl'
                  }`}
                >
                  <RomyIcon size={18} color={location.pathname.startsWith('/chat') ? undefined : 'white'} />
                  <span className={location.pathname.startsWith('/chat') ? 'font-medium' : ''}>{currentUser ? 'Chat' : 'Get Started'}</span>
                </Link>
                {currentUser ? (
                  <UserMenu />
                ) : (
                  <button
                    onClick={openLogin}
                    className="px-3 lg:px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 font-medium transition-colors text-sm lg:text-base"
                  >
                    Sign In
                  </button>
                )}
              </nav>
            </div>

            {/* Mobile layout: only shown when NOT signed in */}
            {!currentUser && (
              <div className="flex md:hidden justify-between items-center h-14">
                <a
                  href={APP_DOWNLOAD_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-medium text-pink-600 dark:text-pink-400 hover:text-pink-700 dark:hover:text-pink-300 transition-colors"
                >
                  Get the App
                </a>

                <Link to="/" className="absolute left-1/2 -translate-x-1/2 flex items-center gap-1.5">
                  <svg width="20" height="20" viewBox="0 0 400 400" fill="none" aria-hidden="true">
                    <defs>
                      <linearGradient id="nav-bm-m" x1="40" y1="360" x2="360" y2="40" gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor="#FF6B9D" />
                        <stop offset="100%" stopColor="#FF8C69" />
                      </linearGradient>
                    </defs>
                    <circle cx="200" cy="200" r="190" fill="url(#nav-bm-m)" />
                    <circle cx="200" cy="159" r="70" fill="white" />
                    <path d="M 200 123 L 214 150 L 241 159 L 214 168 L 200 195 L 186 168 L 159 159 L 186 150 Z" fill="#EC4899" />
                    <rect x="192" y="229" width="16" height="120" rx="8" fill="white" />
                    <path d="M 200 303 C 178 299, 136 279, 116 253" stroke="white" strokeWidth="16" fill="none" strokeLinecap="round" />
                    <path d="M 200 303 C 222 299, 264 279, 284 253" stroke="white" strokeWidth="16" fill="none" strokeLinecap="round" />
                  </svg>
                  <span className="text-xl font-bold bg-gradient-to-r from-orange-500 to-pink-500 bg-clip-text text-transparent tracking-tight">
                    Romy
                  </span>
                </Link>

                <button
                  onClick={openLogin}
                  className="text-xs font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                >
                  Sign In
                </button>
              </div>
            )}
          </div>
        </header>
      )}

      {/* Mobile bottom tab bar */}
      <BottomTabBar />

      <LoginModal isOpen={isLoginOpen} onClose={() => setIsLoginOpen(false)} />
    </>
  );
}
